import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1733665921380-27c7d02f3b0b" alt="Construction site"
             className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#040316]/75" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-display font-black text-4xl tracking-tight leading-tight">Route bids &amp; documents<br/>without the chaos.</h2>
          <p className="mt-3 text-white/80 max-w-md">Connect your inbox, dispatch ITBs by CSI code, and auto-file subcontractor PDFs into the right Box folders.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <p className="font-display font-black tracking-tight text-xl">Bid Router</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground -mt-0.5">by Conduit Systems</p>
            </div>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Sign in to your workspace</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     data-testid="login-email" className="mt-1.5" placeholder="you@company.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary font-medium" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                     data-testid="login-password" className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            New here? <Link to="/register" className="text-primary font-medium" data-testid="go-register">Create a workspace</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
